import { Component } from '@theme/component';
import { debounce, onAnimationEnd } from '@theme/utilities';
import { RecipeAPI, getRecipeAPI } from './recipe-api.js';

/**
 * Recipe Modal Component
 * Handles the recipe creation modal for "Kapky na míru" products
 *
 * @typedef {object} RecipeModalRefs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLButtonElement} closeButton - Close button
 * @property {HTMLFormElement} recipeForm - Recipe form
 * @property {HTMLInputElement} recipeName - Recipe name input
 * @property {HTMLInputElement} healthIssues - Health issues input
 * @property {HTMLInputElement} medications - Medications input
 * @property {HTMLInputElement} medicationsPurpose - Medications purpose input
 * @property {HTMLInputElement} childAge - Child age input
 * @property {HTMLButtonElement} methodList - Method list button
 * @property {HTMLButtonElement} methodText - Method text button
 * @property {HTMLElement} problemsListPanel - Problems list panel
 * @property {HTMLElement} problemsTextPanel - Problems text panel
 * @property {HTMLElement} problemsContainer - Problems checkboxes container
 * @property {HTMLElement} problemsCount - Selected problems count display
 * @property {HTMLTextAreaElement} desiredOutcome - Desired outcome textarea
 * @property {HTMLButtonElement} submitButton - Submit button
 * @property {HTMLElement} submitLoading - Submit loading indicator
 * @property {HTMLElement} formError - Form error message container
 *
 * @extends Component<RecipeModalRefs>
 */
/**
 * @typedef {object} RecipeDraft
 * @property {string} localId - UUID generated on frontend
 * @property {number} [templateRecipeId] - ID of existing recipe (if selected from list)
 * @property {string} [name] - Recipe name
 * @property {'list' | 'text'} [creationMethod] - How the recipe was created
 * @property {number[]} [selectedProblems] - Selected problem IDs
 * @property {string[]} [customProblems] - Custom problem texts
 * @property {string} [healthIssues] - Health issues
 * @property {string} [medications] - Medications
 * @property {string} [medicationsPurpose] - Medications purpose
 * @property {string} [childAge] - Child age (optional, free text)
 * @property {string} [desiredOutcome] - Desired outcome
 * @property {boolean} isEditable - Can be edited
 * @property {string} displayName - Name to display in UI
 */

class RecipeModalComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {RecipeAPI | null} */
  #api = null;

  /** @type {string} */
  #currentMethod = 'text';

  /** @type {number} */
  #maxProblems = 7;

  /** @type {number} */
  #minProblems = 5;

  /** @type {Set<number>} */
  #selectedProblems = new Set();

  /** @type {Array<{id: number, text: string, essences: number[], parent_id?: number, children?: Array<{id: number, text: string, parent_id: number}>}>} */
  #problems = [];

  /** @type {boolean} */
  #problemsLoaded = false;

  /** @type {boolean} */
  #isSearching = false;

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {'create' | 'edit'} */
  #mode = 'create';

  /** @type {RecipeDraft | null} */
  #editingDraft = null;

  connectedCallback() {
    super.connectedCallback();

    // Initialize API
    const apiUrl = this.dataset.apiUrl;
    const locale = this.dataset.locale || 'cs';

    if (apiUrl) {
      this.#api = getRecipeAPI(apiUrl, locale);
    }

    // Get max/min problems from data attributes
    if (this.dataset.maxProblems) {
      this.#maxProblems = parseInt(this.dataset.maxProblems, 10);
    }
    if (this.dataset.minProblems) {
      this.#minProblems = parseInt(this.dataset.minProblems, 10);
    }

    // Setup debounced search (lazy init — only create once)
    if (!this.#debouncedSearch) {
      this.#debouncedSearch = debounce(this.#performSearch.bind(this), 300);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // If dialog is open when disconnected, close it and clean up
    const { dialog } = this.refs;
    if (dialog?.open) {
      this.removeEventListener('click', this.#handleOutsideClick);
      this.removeEventListener('keydown', this.#handleKeyDown);

      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';

      dialog.close();
      dialog.classList.remove('dialog-closing');
    }
  }

  /** @type {Function} */
  #debouncedSearch;

  /**
   * Set modal to create mode (new recipe)
   */
  setCreateMode() {
    this.#mode = 'create';
    this.#editingDraft = null;
    this.#resetForm();
  }

  /**
   * Set modal to edit mode (editing existing draft)
   * @param {RecipeDraft} draft - The draft to edit
   */
  setEditMode(draft) {
    this.#mode = 'edit';
    this.#editingDraft = draft;
    // Vyčistit formulář po předchozím draftu. #prefillForm() dosadí hodnoty
    // z tohoto draftu; bez resetu by v polích zůstaly hodnoty z draftu,
    // který uživatel otevřel a zavřel bez uložení.
    this.#resetForm();
  }

  /**
   * Shows the modal dialog
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();

      // Load problems if not already loaded
      if (!this.#problemsLoaded && this.#api) {
        this.#loadProblems().then(() => {
          // Pre-fill form if in edit mode (after problems are loaded)
          if (this.#mode === 'edit' && this.#editingDraft) {
            this.#prefillForm(this.#editingDraft);
          }
        });
      } else if (this.#mode === 'edit' && this.#editingDraft) {
        // Pre-fill form if problems already loaded
        this.#prefillForm(this.#editingDraft);
      }

      this.addEventListener('click', this.#handleOutsideClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Pre-fill form with draft data
   * @param {RecipeDraft} draft
   */
  #prefillForm(draft) {
    const { recipeName, healthIssues, medications, medicationsPurpose, childAge, desiredOutcome, problemsContainer } = this.refs;

    // Fill text fields
    if (recipeName && draft.name) recipeName.value = draft.name;
    if (healthIssues && draft.healthIssues) healthIssues.value = draft.healthIssues;
    if (medications && draft.medications) medications.value = draft.medications;
    if (medicationsPurpose && draft.medicationsPurpose) medicationsPurpose.value = draft.medicationsPurpose;
    if (childAge && draft.childAge) childAge.value = draft.childAge;
    if (desiredOutcome && draft.desiredOutcome) desiredOutcome.value = draft.desiredOutcome;

    // Set creation method
    if (draft.creationMethod) {
      this.switchMethod(draft.creationMethod);
    }

    // Check problems if list method
    if (draft.creationMethod === 'list' && draft.selectedProblems && problemsContainer) {
      this.#selectedProblems.clear();
      draft.selectedProblems.forEach((id) => this.#selectedProblems.add(id));

      const checkboxes = problemsContainer.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach((checkbox) => {
        const input = /** @type {HTMLInputElement} */ (checkbox);
        const problemId = parseInt(input.value, 10);
        input.checked = this.#selectedProblems.has(problemId);
      });
      this.#updateProblemsCount();
    }

    // Fill custom problems if text method
    if (draft.creationMethod === 'text' && draft.customProblems) {
      draft.customProblems.forEach((text, index) => {
        const input = /** @type {HTMLInputElement | null} */ (
          this.querySelector(`input[name="custom_problem_${index + 1}"]`)
        );
        if (input) input.value = text;
      });
    }
  }

  /**
   * Reset form to initial state
   */
  #resetForm() {
    const { recipeForm } = this.refs;
    if (recipeForm) {
      recipeForm.reset();
    }
    this.#selectedProblems.clear();
    this.#updateProblemsCount();
    this.#uncheckAllProblems();
    this.switchMethod('text');
  }

  /**
   * Closes the modal dialog
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleOutsideClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
  };


  /**
   * Switch between creation methods (list/text)
   * @param {'list'|'text'} method
   */
  switchMethod(method) {
    const { methodList, methodText, problemsListPanel, problemsTextPanel, helpLinkList, helpLinkText } = this.refs;

    this.#currentMethod = method;
    const isList = method === 'list';

    methodList?.classList.toggle('recipe-form__method-btn--active', isList);
    methodText?.classList.toggle('recipe-form__method-btn--active', !isList);

    if (problemsListPanel) problemsListPanel.hidden = !isList;
    if (problemsTextPanel) problemsTextPanel.hidden = isList;

    // Toggle help links
    if (helpLinkList) helpLinkList.hidden = !isList;
    if (helpLinkText) helpLinkText.hidden = isList;
  }

  /**
   * Show help lightbox with screenshot
   * @param {'list'|'text'} method
   * @param {Event} event
   */
  showHelp(method, event) {
    event?.preventDefault();

    const { helpLightbox, helpImage, helpLinkList, helpLinkText } = this.refs;
    if (!helpLightbox || !helpImage) return;

    const link = method === 'list' ? helpLinkList : helpLinkText;
    const imageUrl = link?.dataset.helpImage;

    if (!imageUrl) return;

    helpImage.src = imageUrl;
    helpLightbox.hidden = false;
  }

  /**
   * Close help lightbox
   */
  closeHelp() {
    const { helpLightbox } = this.refs;
    if (helpLightbox) {
      helpLightbox.hidden = true;
    }
  }

  /**
   * Handle email search input
   * @param {Event} event
   */
  handleSearchInput(event) {
    this.#debouncedSearch();
  }

  /**
   * Handle search button click
   */
  handleSearch() {
    this.#performSearch();
  }

  /**
   * Perform the actual search
   */
  async #performSearch() {
    if (this.#isSearching) return;

    const { searchEmail, searchResults, searchLoading, searchEmpty } = this.refs;

    if (!this.#api || !searchEmail) return;

    const email = searchEmail.value.trim();

    if (!email || !email.includes('@')) {
      if (searchResults) searchResults.innerHTML = '';
      if (searchEmpty) searchEmpty.hidden = true;
      return;
    }

    this.#isSearching = true;

    // Show loading
    if (searchLoading) searchLoading.hidden = false;
    if (searchEmpty) searchEmpty.hidden = true;
    if (searchResults) searchResults.innerHTML = '';

    try {
      const recipes = await this.#api.searchRecipes(email);

      if (searchLoading) searchLoading.hidden = true;

      if (recipes.length === 0) {
        if (searchEmpty) searchEmpty.hidden = false;
        return;
      }

      // Render search results
      if (searchResults) {
        searchResults.innerHTML = recipes
          .map(
            (recipe) => `
          <div class="recipe-result" data-recipe-id="${recipe.id}" data-recipe-name="${this.#escapeHtml(recipe.name)}">
            <div class="recipe-result__header">
              <span class="recipe-result__name">${this.#escapeHtml(recipe.name)}</span>
              <span class="recipe-result__date">${this.#formatDate(recipe.created_at)}</span>
            </div>
            <p class="recipe-result__summary">${this.#escapeHtml(recipe.problems_summary || recipe.custom_problems_summary || '')}</p>
            <button type="button" class="button button-secondary recipe-result__select" on:click="/selectExistingRecipe?id=${recipe.id}&name=${encodeURIComponent(recipe.name)}">
              ${this.dataset.selectText || 'Vybrat tento recept'}
            </button>
          </div>
        `
          )
          .join('');
      }
    } catch (error) {
      console.warn('[MEG] Recipe search failed:', error);
      if (searchLoading) searchLoading.hidden = true;
      if (searchEmpty) {
        searchEmpty.hidden = false;
        searchEmpty.textContent = this.dataset.errorText || 'Chyba při vyhledávání';
      }
    } finally {
      this.#isSearching = false;
    }
  }

  /**
   * Select an existing recipe from search results
   * @param {{ id: number, name: string }} data
   */
  selectExistingRecipe(data) {
    const recipeName = decodeURIComponent(data.name);

    // Create a draft that references the existing recipe (will be copied on API when adding to cart)
    /** @type {RecipeDraft} */
    const draft = {
      localId: this.#generateLocalId(),
      templateRecipeId: data.id,
      name: recipeName,
      isEditable: false, // Existing recipes cannot be edited
      displayName: recipeName,
    };

    this.#dispatchRecipeCreated(draft);
    this.closeDialog();
  }

  /**
   * Handle problem checkbox change
   * @param {Event} event
   */
  handleProblemChange(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const problemId = parseInt(target.value, 10);
    const isMainProblem = target.dataset.isMain === 'true';
    const parentId = target.dataset.parentId;

    if (target.checked) {
      // Only check limit for main problems
      if (isMainProblem && this.#getMainProblemsCount() >= this.#maxProblems) {
        target.checked = false;
        this.#showMaxProblemsError();
        return;
      }
      this.#selectedProblems.add(problemId);

      // If main problem, show its sub-problems
      if (isMainProblem) {
        this.#toggleSubProblems(problemId, true);
      }
    } else {
      this.#selectedProblems.delete(problemId);

      // If main problem, hide and uncheck its sub-problems
      if (isMainProblem) {
        this.#toggleSubProblems(problemId, false);
        this.#uncheckSubProblems(problemId);
      }
    }

    this.#updateProblemsCount();
  }

  /**
   * Get count of selected main problems (without parent_id)
   * @returns {number}
   */
  #getMainProblemsCount() {
    let count = 0;
    for (const id of this.#selectedProblems) {
      // Check if this ID is a main problem (exists at top level)
      if (this.#problems.some((p) => p.id === id)) {
        count++;
      }
    }
    return count;
  }

  /**
   * Toggle visibility of sub-problems
   * @param {number} parentId
   * @param {boolean} show
   */
  #toggleSubProblems(parentId, show) {
    const { problemsContainer } = this.refs;
    if (!problemsContainer) return;

    const subProblemsContainer = problemsContainer.querySelector(`[data-parent-id="${parentId}"]`);
    if (subProblemsContainer) {
      subProblemsContainer.hidden = !show;
    }
  }

  /**
   * Uncheck all sub-problems for a parent
   * @param {number} parentId
   */
  #uncheckSubProblems(parentId) {
    const { problemsContainer } = this.refs;
    if (!problemsContainer) return;

    const subProblemsContainer = problemsContainer.querySelector(`[data-parent-id="${parentId}"]`);
    if (subProblemsContainer) {
      const checkboxes = subProblemsContainer.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach((checkbox) => {
        const input = /** @type {HTMLInputElement} */ (checkbox);
        if (input.checked) {
          input.checked = false;
          this.#selectedProblems.delete(parseInt(input.value, 10));
        }
      });
    }
  }

  /**
   * Update problems count display (only counts main problems)
   */
  #updateProblemsCount() {
    const { problemsCount } = this.refs;
    if (problemsCount) {
      const mainCount = this.#getMainProblemsCount();
      problemsCount.textContent = `${mainCount}/${this.#maxProblems}`;
    }
  }

  /**
   * Show max problems error
   */
  #showMaxProblemsError() {
    const { formError } = this.refs;
    if (formError) {
      formError.textContent = this.dataset.maxProblemsError || `Lze vybrat maximálně ${this.#maxProblems} problémů`;
      formError.hidden = false;
      setTimeout(() => {
        formError.hidden = true;
      }, 3000);
    }
  }

  /**
   * Generate a unique local ID for drafts
   * @returns {string}
   */
  #generateLocalId() {
    return 'draft_' + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Handle form submission
   * @param {Event} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { recipeForm, submitButton, submitLoading, formError } = this.refs;

    if (!recipeForm) return;

    // Validate form
    if (!recipeForm.checkValidity()) {
      recipeForm.reportValidity();
      return;
    }

    // Validate problems selection for list method - require between min and max
    if (this.#currentMethod === 'list') {
      const mainProblemsCount = this.#getMainProblemsCount();
      if (mainProblemsCount < this.#minProblems || mainProblemsCount > this.#maxProblems) {
        if (formError) {
          formError.textContent =
            this.dataset.rangeProblemsError ||
            `Vyberte ${this.#minProblems} až ${this.#maxProblems} problémů (máte ${mainProblemsCount})`;
          formError.hidden = false;
        }
        return;
      }
    }

    // Validate text problems for text method - require between min and max filled fields
    if (this.#currentMethod === 'text') {
      const formData = new FormData(recipeForm);
      let filledCount = 0;
      for (let i = 1; i <= 7; i++) {
        const value = formData.get(`custom_problem_${i}`)?.toString()?.trim();
        if (value) filledCount++;
      }
      if (filledCount < this.#minProblems || filledCount > this.#maxProblems) {
        if (formError) {
          formError.textContent =
            this.dataset.rangeTextProblemsError ||
            `Vyplňte ${this.#minProblems} až ${this.#maxProblems} problémů (máte ${filledCount})`;
          formError.hidden = false;
        }
        return;
      }
    }

    // Show loading briefly for UX
    if (submitButton) submitButton.disabled = true;
    if (submitLoading) submitLoading.hidden = false;
    if (formError) formError.hidden = true;

    try {
      const formData = new FormData(recipeForm);

      // Collect custom problems for text method
      /** @type {string[] | undefined} */
      let customProblems;
      if (this.#currentMethod === 'text') {
        customProblems = [];
        for (let i = 1; i <= 7; i++) {
          const value = formData.get(`custom_problem_${i}`)?.toString();
          if (value) customProblems.push(value);
        }
        if (customProblems.length === 0) customProblems = undefined;
      }

      // Create draft object (not sent to API yet)
      /** @type {RecipeDraft} */
      const draft = {
        localId: this.#editingDraft?.localId || this.#generateLocalId(),
        name: formData.get('recipe_name')?.toString() || '',
        creationMethod: /** @type {'list' | 'text'} */ (this.#currentMethod),
        selectedProblems: this.#currentMethod === 'list' ? Array.from(this.#selectedProblems) : undefined,
        customProblems: customProblems,
        healthIssues: formData.get('health_issues')?.toString() || undefined,
        medications: formData.get('medications')?.toString() || undefined,
        medicationsPurpose: formData.get('medications_purpose')?.toString() || undefined,
        childAge: formData.get('child_age')?.toString() || undefined,
        desiredOutcome: formData.get('desired_outcome')?.toString() || undefined,
        isEditable: true,
        displayName: formData.get('recipe_name')?.toString() || '',
      };

      // Dispatch draft created event (not sent to API yet - will be sent when adding to cart)
      this.#dispatchRecipeCreated(draft);
      this.closeDialog();

      // Reset form and mode
      this.#resetForm();
      this.#mode = 'create';
      this.#editingDraft = null;
    } catch (error) {
      console.warn('[MEG] Recipe creation failed:', error);
      if (formError) {
        formError.textContent =
          error instanceof Error ? error.message : this.dataset.submitError || 'Chyba při ukládání receptu';
        formError.hidden = false;
      }
    } finally {
      if (submitButton) submitButton.disabled = false;
      if (submitLoading) submitLoading.hidden = true;
    }
  }

  /**
   * Dispatch recipe created event with draft data
   * @param {RecipeDraft} draft
   */
  #dispatchRecipeCreated(draft) {
    this.dispatchEvent(
      new CustomEvent('recipe:created', {
        bubbles: true,
        detail: draft,
      })
    );
  }

  /**
   * Load problems from API
   */
  async #loadProblems() {
    if (!this.#api) return;

    const { problemsContainer } = this.refs;

    try {
      this.#problems = await this.#api.getProblems();
      this.#problemsLoaded = true;
      this.#renderProblems();
    } catch (error) {
      console.warn('[MEG] Failed to load problems:', error);
      // Show error in problems container
      if (problemsContainer) {
        problemsContainer.innerHTML = `
          <div class="recipe-form__problems-error">
            <p>${this.dataset.loadProblemsError || 'Nepodařilo se načíst seznam problémů. Zkuste to prosím později.'}</p>
            <button type="button" class="button button-secondary" on:click="/retryLoadProblems">
              ${this.dataset.retryText || 'Zkusit znovu'}
            </button>
          </div>
        `;
      }
    }
  }

  /**
   * Retry loading problems
   */
  retryLoadProblems() {
    const { problemsContainer } = this.refs;
    if (problemsContainer) {
      problemsContainer.innerHTML = '<div class="recipe-form__problems-loading"><span class="recipe-form__spinner"></span></div>';
    }
    this.#loadProblems();
  }

  /**
   * Render problems checkboxes with hierarchical structure
   */
  #renderProblems() {
    const { problemsContainer } = this.refs;
    if (!problemsContainer) return;

    problemsContainer.innerHTML = this.#problems
      .map((problem) => {
        // Render main problem
        let html = `
        <label class="recipe-modal__problem" data-problem-id="${problem.id}">
          <input
            type="checkbox"
            name="problems[]"
            value="${problem.id}"
            data-is-main="true"
            on:change="/handleProblemChange"
          >
          <span class="recipe-modal__problem-text">${this.#escapeHtml(problem.text)}</span>
        </label>`;

        // Render sub-problems (children) if they exist
        if (problem.children && problem.children.length > 0) {
          html += `<div class="recipe-modal__sub-problems" data-parent-id="${problem.id}" hidden>`;
          html += problem.children
            .map(
              (child) => `
            <label class="recipe-modal__problem recipe-modal__problem--sub" data-problem-id="${child.id}">
              <input
                type="checkbox"
                name="problems[]"
                value="${child.id}"
                data-is-main="false"
                data-parent-id="${problem.id}"
                on:change="/handleProblemChange"
              >
              <span class="recipe-modal__problem-text">${this.#escapeHtml(child.text)}</span>
            </label>
          `
            )
            .join('');
          html += `</div>`;
        }

        return html;
      })
      .join('');
  }

  /**
   * Uncheck all problem checkboxes and hide sub-problems
   */
  #uncheckAllProblems() {
    const { problemsContainer } = this.refs;
    if (!problemsContainer) return;

    // Uncheck all checkboxes
    const checkboxes = problemsContainer.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      /** @type {HTMLInputElement} */ (checkbox).checked = false;
    });

    // Hide all sub-problems containers
    const subProblemsContainers = problemsContainer.querySelectorAll('.recipe-modal__sub-problems');
    subProblemsContainers.forEach((container) => {
      container.hidden = true;
    });

    // Clear selected problems set
    this.#selectedProblems.clear();
  }

  /**
   * Handle click outside dialog
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    const { dialog } = this.refs;
    if (event.target === dialog) {
      this.closeDialog();
    }
  };

  /**
   * Handle escape key
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      // Close lightbox first if open, otherwise close dialog
      const { helpLightbox } = this.refs;
      if (helpLightbox && !helpLightbox.hidden) {
        this.closeHelp();
      } else {
        this.closeDialog();
      }
    }
  };

  /**
   * Escape HTML to prevent XSS
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Format date string
   * @param {string} dateStr
   * @returns {string}
   */
  #formatDate(dateStr) {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(this.dataset.locale || 'cs', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }
}

if (!customElements.get('recipe-modal-component')) {
  customElements.define('recipe-modal-component', RecipeModalComponent);
}

export { RecipeModalComponent };
